import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';

import './styles.css';
import { AcademicMatch } from '@purple/match-graphql-schema/lib/schema/supermatch/enums';
import getRestService from '../../../restService';
import AcadmicMatchLegend from './legend';
import AcademicMatchGraph from './academicMatchGraph';
import { parseTestScores, getUserSATTotalScore } from '../../../utils/studentProfiles';
import { testScoresByOption, schoolAcceptedTestScoreKeys } from './constants';

const messages = defineMessages({
  heading: {
    id: 'hubs.overview.academic-match.heading',
    defaultMessage: 'How you compare with others who have applied to {college}',
  },
  subHeading: {
    id: 'hubs.overview.academic-match.sub-heading',
    defaultMessage:
      'This institution is a {match}: Your academic qualifications (GPA & test scores) {matchDetails} the academic profile of students nationally who are typically enrolled at this institution.',
  },
  matchDetails: {
    id: 'hubs.overview.academic-match.matchDetails',
    defaultMessage: 'are similar to',
  },
  reachDetails: {
    id: 'hubs.overview.academic-match.reachDetails',
    defaultMessage: 'fall below',
  },
  safetyDetails: {
    id: 'hubs.overview.academic-match.safetyDetails',
    defaultMessage: 'are above',
  },
});

const emptyAcceptedObject = {
  act: {},
  sat: {},
  gpa: {},
  gpaConverted: {},
  weightedGpa: {},
  weightedGpaConverted: {},
};

class AcademicMatchComponent extends React.PureComponent {
  static propTypes = {
    userData: PropTypes.object,
    featureFlags: PropTypes.object,
    highSchool: PropTypes.object,
    fullProfile: PropTypes.object,
    applicationStatistics: PropTypes.object,
    session: PropTypes.object,
    apiUrl: PropTypes.string,
    testScores: PropTypes.arrayOf(PropTypes.object),
    intl: PropTypes.object,
  };

  matchLabels = {
    [AcademicMatch.REACH]: 'Reach',
    [AcademicMatch.MATCH]: 'Match',
    [AcademicMatch.SAFETY]: 'Safety',
  };

  matchToMatchDetails = {
    [AcademicMatch.REACH]: this.props.intl.formatMessage(messages.reachDetails),
    [AcademicMatch.MATCH]: this.props.intl.formatMessage(messages.matchDetails),
    [AcademicMatch.SAFETY]: this.props.intl.formatMessage(messages.safetyDetails),
  };

  state = {
    academicMatch: null,
    academicMatchRetrieved: false,
  };

  restService = null;

  async componentDidMount() {
    await this.getAcademicMatch();
  }

  componentDidUpdate() {
    const { academicMatchRetrieved } = this.state;
    /* istanbul ignore if */
    if (!academicMatchRetrieved) {
      this.getAcademicMatch();
    }
  }

  async getAcademicMatch() {
    /* istanbul ignore next */
    const { highSchool, applicationStatistics, userData, testScores } = this.props;
    /* istanbul ignore else */
    if (!this.restService) {
      const { apiUrl, session } = this.props;
      this.restService = getRestService({ session, uri: apiUrl });
    }
    const defaultGPA = get(highSchool, 'hsoptions.defaultGPA', 0);
    const gpaType = defaultGPA === 0 ? 'cumulativeGpa' : 'weightedGpa';
    const gpa = userData[gpaType];

    const { sat } = getUserSATTotalScore(applicationStatistics, userData, testScores);
    const act = parseTestScores('ACT', 'total', 'act', testScores);

    await this.restService
      .sendRequest(
        `/scid/${this.props.scid}/academic-match?gpa=${gpa}&sat=${sat}&act=${act}`,
        { method: 'GET' },
      )
      .then((response) => response.json())
      .then((response) => {
        this.setState(Object.assign(response, { academicMatchRetrieved: true }));
      })
      .catch(
        /* istanbul ignore next */ (error) => {
          const errorMessage = error.message; //=> String
          console.log(`ERROR: ${errorMessage}`);
        },
      );
  }

  isValidData = (graphData) => {
    const national25gpa = get(graphData, 'national.gpa[25].value');
    const national75gpa = get(graphData, 'national.gpa[25].value');
    /* istanbul ignore else */
    if (!isEmpty(pickBy(graphData.you)) || national25gpa || national75gpa) {
      return true;
    }

    let valid = false;
    const scoreOptions = Object.keys(testScoresByOption);
    for (let i = 0; i < scoreOptions.length; i++) {
      const testScores = get(graphData, `national.testScores`, []).find(
        (ts) => ts && ts.type === testScoresByOption[scoreOptions[i]],
      );
      /* istanbul ignore next */
      if (testScores && (testScores[25] || testScores[75])) {
        valid = true;
        break;
      }
    }
    if (valid) {
      return valid;
    }

    const accepted = get(graphData, `applicationStatistics.accepted`, null);
    for (let i = 0; i < schoolAcceptedTestScoreKeys.length; i++) {
      const val = get(accepted, schoolAcceptedTestScoreKeys[i]);
      /* istanbul ignore next */
      if (val && (val[25] || val[75])) {
        valid = true;
        break;
      }
    }

    return valid;
  };

  render() {
    const {
      fullProfile,
      userData,
      highSchool,
      applicationStatistics,
      featureFlags,
      testScores,
    } = this.props;

    /* istanbul ignore if */
    if (!testScores) return null;

    const collegeName = get(fullProfile, 'overview.schoolName.value', '');
    const { academicMatch: match } = this.state;
    const matchDetails = match ? this.matchToMatchDetails[match] : '';
    const { gpa, testScores: collegeTestScores } = fullProfile.overview;
    const { gpa: cumulativeGpa, gpaConverted: cumulativeGpaConverted } = get(
      applicationStatistics,
      'applicationStatistics.accepted',
      emptyAcceptedObject,
    );
    const accepted = get(applicationStatistics, 'applicationStatistics.accepted');
    /* istanbul ignore else */
    if (accepted) {
      applicationStatistics.applicationStatistics.accepted = Object.assign({}, accepted, {
        cumulativeGpa,
        cumulativeGpaConverted,
      });
    }

    const useGpaConcordance = get(featureFlags, 'featureHubsGpaMapping', false);
    const fixGpaBarGraphWithoutPeerMap = get(
      featureFlags,
      'featureMatchHubsFixGpaBarGraphWithoutPeerMap',
      false,
    );

    const { sat, usePsat } = getUserSATTotalScore(
      applicationStatistics,
      userData,
      testScores,
    );

    const graphData = {
      useGpaConcordance: useGpaConcordance,
      fixGpaBarGraphWithoutPeerMap: fixGpaBarGraphWithoutPeerMap,
      usePsat: usePsat,
      gpaConcordance: useGpaConcordance ? get(userData, 'gpaConcordance', null) : null,
      you: {
        cumulativeGpa: get(applicationStatistics, 'userInfo.academics.rawCumulativeGpa'),
        cumulativeGpaConverted: get(applicationStatistics, 'userInfo.academics.gpa'),
        weightedGpa: get(applicationStatistics, 'userInfo.academics.rawWeightedGpa'),
        weightedGpaConverted: get(
          applicationStatistics,
          'userInfo.academics.weightedGpa',
        ),
        satTotal: sat,
        satMath: parseTestScores(
          usePsat ? 'PSAT' : 'SAT',
          usePsat ? 'mathematics' : 'mathSection',
          '',
          testScores,
        ),
        satReadingWriting: parseTestScores(
          usePsat ? 'PSAT' : 'SAT',
          'evBasedReadWrite',
          '',
          testScores,
        ),
        actComposite: parseTestScores('ACT', 'total', 'act', testScores),
        actEnglish: parseTestScores('ACT', 'english', '', testScores),
        actMath: parseTestScores('ACT', 'math', '', testScores),
        actReading: parseTestScores('ACT', 'reading', '', testScores),
        actScience: parseTestScores('ACT', 'science', '', testScores),
      },
      national: {
        gpa,
        testScores: collegeTestScores,
      },
      applicationStatistics: {
        ...get(applicationStatistics, 'applicationStatistics', {}),
      },
      highSchool,
    };

    return this.isValidData(graphData) ? (
      <div
        className="academic-match-container common-full-width"
        aria-label="Academic Match section"
      >
        <div className="common-core-width">
          <h2
            data-cy="academic-match-heading"
            className="academic-match-heading"
            tabIndex="0"
          >
            <FormattedMessage {...messages.heading} values={{ college: collegeName }} />
          </h2>
          {match && (
            <p
              data-cy="academic-match-sub-heading"
              className="academic-match-sub-heading"
              tabIndex="0"
            >
              <FormattedMessage
                {...messages.subHeading}
                values={{
                  match: <b>{this.matchLabels[match]}</b>,
                  matchDetails: <b>{matchDetails}</b>,
                }}
              />
            </p>
          )}
          <AcadmicMatchLegend />
          <div className="academic-match-graphs-container">
            <AcademicMatchGraph name="GPA" graphData={graphData} />
            <AcademicMatchGraph name="SAT" graphData={graphData} />
            <AcademicMatchGraph name="ACT" graphData={graphData} />
          </div>
        </div>
      </div>
    ) : null;
  }
}

export default injectIntl(AcademicMatchComponent);
